import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { zodResolver } from '@hookform/resolvers/zod';
import ModalActionBar from 'components/modal-action-bar';
import SelectField from 'components/select-field';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewOrderPartyType } from 'types/order';
import validations from 'utils/validations';
import SearchContact from 'views/orders/new-charges/components/searchContact';
import { z } from 'zod';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(0, 2, 2),
  },
  title: {
    padding: theme.spacing(2, 2, 1),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    width: '60%',
    color: theme.palette.primary.darker,
  },
}));

interface PartyModalAssociationsProps {
  open: boolean;
  handleClose: () => void;
  listingEntries: ListingEntry[];
  handleSubmitAssociation: (data: FormValueAssociation) => void;
  partySelected: NewOrderPartyType;
  isSubmitting?: boolean;
}

export type FormValueAssociation = {
  partyAssociationId: string;
  role: string;
  kind: string;
};

const PartyModalAssociations: FC<PartyModalAssociationsProps> = ({
  open,
  handleClose,
  listingEntries,
  handleSubmitAssociation,
  partySelected,
  isSubmitting = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const schema = z.object({
    role: validations.requiredString('required'),
    partyAssociationId: validations.requiredString('required'),
    kind: validations.requiredString('required'),
  });

  const defaultKind = partySelected.kinds[0];

  const {
    control,
    setValue,
    trigger,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValueAssociation>({
    defaultValues: {
      role: '',
      partyAssociationId: '',
      kind: defaultKind,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!open) {
      reset({
        role: '',
        partyAssociationId: '',
        kind: defaultKind,
      });
    }
  }, [open, reset, defaultKind]);

  const handleCloseModal = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const onSubmit = (data: FormValueAssociation) => {
    handleSubmitAssociation(data);
  };

  return (
    <Dialog
      onClose={handleCloseModal}
      open={open}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle className={classes.title}>{t('orders:relationsTitle')}</DialogTitle>
      <DialogContent className={classes.container}>
        <Box className={classes.fieldRow}>
          <Typography variant="body2" className={classes.fieldLabel}>
            {partySelected.name}
          </Typography>
          <Controller
            control={control}
            name="kind"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectField
                {...field}
                emptyValue
                value={partySelected.kinds.length > 0 ? field.value : ''}
                inputRef={field.ref}
                data={[...partySelected.kinds].sort((entryA, entryB) => entryA.localeCompare(entryB))}
                dataKey=""
                dataValue=""
                dataText=""
                label={t('orders:kindLabel')}
                handleChange={field.onChange}
              />
            )}
          />
        </Box>

        <Box className={classes.fieldRow}>
          <Typography variant="body2" className={classes.fieldLabel}>
            {t('orders:willHaveLabel')}
          </Typography>
          <Controller
            control={control}
            name="partyAssociationId"
            render={({ field }) => (
              <SearchContact
                reference={field.ref}
                getValue={(contact) => {
                  setValue('partyAssociationId', contact?._id!);
                  trigger('partyAssociationId');
                }}
                initialValue={{ name: '', _id: '' }}
              />
            )}
          />
        </Box>

        <Box className={classes.fieldRow}>
          <Typography variant="body2" className={classes.fieldLabel}>
            {t('orders:asLabel')}
          </Typography>
          <Controller
            control={control}
            name="role"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectField
                {...field}
                emptyValue
                value={listingEntries.length > 0 ? field.value : ''}
                inputRef={field.ref}
                data={[...listingEntries].sort((entryA, entryB) =>
                  entryA.description.localeCompare(entryB.description)
                )}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('orders:kindLabel')}
                handleChange={field.onChange}
              />
            )}
          />
        </Box>
      </DialogContent>
      <ModalActionBar
        onCancel={handleCloseModal}
        submitText={t('common:save')}
        submitType="button"
        onSubmit={handleSubmit(onSubmit)}
        isSubmitting={isSubmitting}
        submitDisabled={!isValid}
      />
    </Dialog>
  );
};

export default PartyModalAssociations;
